<template>
  <video
    ref="videoRef"
    class="rounded-md aspect-video w-full object-cover"
    muted
    autoplay
    loop
    playsinline
    preload="metadata"
    aria-hidden="true"
  >
    <source v-if="sources.webm" :src="sources.webm" type="video/webm" />
    <source :src="sources.mp4" type="video/mp4" />
  </video>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'

import { noop } from '@backmarket/utils/function/noop'

import type { VideoSources } from '~/scopes/campaign/data/videos'

// The campaign's *decorative* self-hosted player — a silent looping clip, used where a
// still image would otherwise go (the intro's tutorial step). Deliberately not a variant
// of `NativeVideo`: no cinema, no FLIP, no `?video=` param, no controls and no play glyph,
// so it needs neither `useModalState` nor the cinema-claims registry. See
// `.claude/rules/video.md`.
//
// `loop` is the native attribute here. `NativeVideo` loops by hand because it needs an
// organic `ended` to close cinema mode and restart muted; nothing listens for `ended` on a
// clip that only ever plays in place, so the browser can own the wrap-around.
//
// Unlike `ImageFigure`, this box crops: the clip is authored to fill a 16:9 slot.

const props = defineProps<{
  /** Self-hosted sources: `webm` preferred where supported, `mp4` fallback. */
  sources: VideoSources
}>()

const videoRef = ref<HTMLVideoElement | null>(null)

function play() {
  const video = videoRef.value
  if (!video) return
  // Belt and braces on top of the static attribute: Vue's binding for `muted` is flaky,
  // and a video the browser doesn't believe is muted is refused autoplay outright.
  video.muted = true
  void video.play().catch(noop)
}

onMounted(play)

// A locale switch rewrites `sources` in place, and changing `src` does not reload a
// <video> — it needs `load()`. See the same watcher in `NativeVideo`.
watch(
  () => props.sources.mp4,
  () => {
    videoRef.value?.load()
    play()
  },
)
</script>
